import React, { useEffect, useState, useRef } from "react"
import Skeleton from "react-loading-skeleton"
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import { LineChart } from "@mui/x-charts"
import moment from "moment"
import { Button } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import ChartInfo from "./ChartInfo"

const INITIAL_FILTERS = { date_from: null, date_to: null }

function IncomesAndExpenses() {
  const [loading, setLoading] = useState(true)
  const [labels, setLabels] = useState([])
  const [revenueHistorical, setRevenueHistorical] = useState([])
  const [revenuePredicted, setRevenuePredicted] = useState([])
  const [expensesHistorical, setExpensesHistorical] = useState([])
  const [expensesPredicted, setExpensesPredicted] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)

        const f = `${filters.date_from ? `&date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`


    Api.get(`analytics/income-expense-prediction?${f}`)
      .then((res) => {
        const cd = res.data.chart_data || {}
        setLabels(
          cd.labels?.map((date) => {
            return moment(date, "YYYY-MM").isSame(moment(), "month")
              ? trans("analytics.today")
              : moment(date, "YYYY-MM").format("MM.YYYY")
          }) || [],
        )
        setRevenueHistorical(cd.revenue_historical || [])
        setRevenuePredicted(cd.revenue_predicted || [])
        setExpensesHistorical(cd.expenses_historical || [])
        setExpensesPredicted(cd.expenses_predicted || [])
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  function buildSeries(historical, predicted) {
    const boundaryIndex = historical.length - 1
    const historicalSeries = labels.map((_, i) =>
      i < historical.length ? (historical[i] ?? null) : null,
    )
    const predictedSeries = labels.map((_, i) => {
      if (i === boundaryIndex) return historical[boundaryIndex] ?? null
      if (i > boundaryIndex) return predicted[i - historical.length] ?? null
      return null
    })
    return { historicalSeries, predictedSeries }
  }

  const { historicalSeries: revHistSeries, predictedSeries: revPredSeries } =
    buildSeries(revenueHistorical, revenuePredicted)
  const { historicalSeries: expHistSeries, predictedSeries: expPredSeries } =
    buildSeries(expensesHistorical, expensesPredicted)

  function hasAnyData() {
    return labels.length > 0
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    return filters.date_from || filters.date_to ? 1 : 0
  }

  const formatValue = (v) => {
    if (v == null) return ""
    if (Math.abs(v) > 1000) {
      const k = v / 1000
      return `${Number.isInteger(k) ? k : k.toFixed(1)}k`
    }
    return String(v)
  }

  const fmt = (v) => formatValue(v)

  return (
    <div className="repair-requests-chart">
      <div className="head">
        <h3>
          {trans("analytics.incomesAndExpenses")}
          <ChartInfo name="incomeExpensePrediction" />
        </h3>
        <div className="right">
          <Button onClick={(e) => { compactFilterLayoutRef.current.open(e) }}>
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? <i>{getAppliedFiltersCount()}</i> : <></>}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className='row'><div className='col'>
              <DateRange
                startDate={filters.date_from || ''}
                endDate={filters.date_to || ''}
                onStartDateChange={(date) => handleFilterChange('date_from', date)}
                onEndDateChange={(date) => handleFilterChange('date_to', date)}
                monthOnly
              />
            </div></div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : !hasAnyData() ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <LineChart
          xAxis={[{ scaleType: "point", data: labels }]}
          yAxis={[{ valueFormatter: formatValue }]}
          series={[
            {
              id: "rev-hist",
              label: trans("analytics.revenueHistorical"),
              area: true,
              data: revHistSeries,
              color: "#89B9B9",
              valueFormatter: fmt,
            },
            {
              id: "rev-pred",
              label: trans("analytics.revenueForecast"),
              data: revPredSeries,
              color: "#89B9B9",
              valueFormatter: fmt,
            },
            {
              id: "exp-hist",
              label: trans("analytics.expensesHistorical"),
              data: expHistSeries,
              color: "#FF718B",
              area: true,
              valueFormatter: fmt,
            },
            {
              id: "exp-pred",
              label: trans("analytics.expensesForecast"),
              data: expPredSeries,
              color: "#FF718B",
              valueFormatter: fmt,
            },
          ]}
          height={400}
          sx={{
            "& .MuiLineElement-series-rev-pred": { strokeDasharray: "6 3" },
            "& .MuiLineElement-series-exp-pred": { strokeDasharray: "6 3" },
            "& .MuiAreaElement-series-rev-hist": {
              fill: "url(#rev-hist-gradient)",
            },
            "& .MuiAreaElement-series-exp-hist": {
              fill: "url(#exp-hist-gradient)",
            },
            "& .MuiLineElement-root": { strokeWidth: 1 },
          }}
          slotProps={{
            legend: { hidden: true },
          }}
        >
          <defs>
            <linearGradient id="rev-hist-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#89B9B9" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#89B9B9" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="exp-hist-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FF718B" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#FF718B" stopOpacity={0.02} />
            </linearGradient>
          </defs>
        </LineChart>
      )}
    </div>
  )
}

export default IncomesAndExpenses
